/**
 * signature_controller.js — the SWMS crew roster's signing pad (Stimulus).
 *
 * The pad itself lives in signature_pad_controller.js, which this extends; only
 * the SWMS-specific part is here. One instance spans the whole roster
 * (data-controller="signature"), not one per form, because there is one pad and
 * it serves two modes:
 *
 *   member  — a crew member on the roster signs; the form posts to signUrl.
 *   casual  — someone not on the team signs; name and role appear and the same
 *             form re-points at casualUrl.
 *
 * The mode flip reveals fields and re-points the action rather than creating a
 * second pad, so there is never a stale canvas hidden behind the visible one.
 * The casual fields stay `disabled` while hidden so a member-mode post omits
 * them entirely rather than sending empty strings the schema would have to
 * reject.
 *
 * Targets beyond the pad's own: form, casualFields, signerName, submitLabel,
 * addButton, heading. Stimulus merges these with the inherited canvas / input /
 * submit.
 */

import SignaturePadController from "./signature_pad_controller.js";

export default class extends SignaturePadController {
    static get targets() {
        return ['form', 'casualFields', 'signerName', 'submitLabel',
                'addButton', 'heading'];
    }
    static get values() { return { signUrl: String, casualUrl: String }; }

    connect() {
        // Captured before the pad binds, and outside its no-canvas bail: a
        // finalised SWMS renders the roster with a heading and no pad at all.
        if (this.hasHeadingTarget) this._memberHeading = this.headingTarget.textContent;
        super.connect();
    }

    // The roster's "add someone not on your team" button flips the one pad
    // into non-member mode; Cancel flips it back.
    enableCasual() {
        this._setCasual(true);
        if (this.hasSignerNameTarget) this.signerNameTarget.focus();
        this.canvasTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    disableCasual() {
        this._setCasual(false);
    }

    // Casual fields stay disabled while hidden so member-mode posts omit them.
    _setCasual(casual) {
        this.casualFieldsTarget.classList.toggle('d-none', !casual);
        this.casualFieldsTarget.querySelectorAll('input').forEach((el) => {
            el.disabled = !casual;
        });
        if (this.hasSignerNameTarget) this.signerNameTarget.required = casual;
        if (this.hasFormTarget) {
            this.formTarget.action = casual ? this.casualUrlValue : this.signUrlValue;
        }
        if (this.hasAddButtonTarget) this.addButtonTarget.classList.toggle('d-none', casual);
        if (this.hasHeadingTarget) {
            this.headingTarget.textContent = casual ? 'Non-member signature' : this._memberHeading;
        }
        if (this.hasSubmitLabelTarget) {
            this.submitLabelTarget.textContent = casual ? 'Record signature' : 'Submit signature';
        }
    }
}
